import { useEffect, useRef } from "react";

// Keeps the /render browser tab from being auto-discarded ("Memory Saver" in
// Chrome, "Efficiency mode" in Edge, tab unloading in Firefox). Browsers unload
// an inactive BACKGROUND tab to reclaim memory and reload it from scratch when
// you return — which users experience as the tab surprise-refreshing after a few
// minutes away, dropping their live Arcware session.
//
// The one signal every Chromium/Firefox build treats as "keep this tab, it's
// doing something": the tab is producing audio. So we emit an inaudible tone —
// low amplitude, sub-bass frequency — that a person can't hear but that still
// registers above the browser's silence threshold, so the tab counts as audible
// and is protected. During active streaming the Arcware WebRTC audio already
// does this; this hook covers the gaps (loading, the login/idle screens, and
// after an AFK/connection drop) where there's otherwise no audio.
//
// Details that matter:
//  - The tone only runs while the tab is HIDDEN. Foreground tabs are never
//    discarded, so there's nothing to protect and nothing for the user to hear
//    in normal use; a hidden tab is one the user has already switched away from.
//  - The AudioContext is created/resumed only after a real user gesture, to
//    satisfy autoplay policy (creating one earlier just logs a console warning
//    and stays suspended until a gesture anyway).
//  - On mount we also report (once) whether THIS load was itself a discard —
//    document.wasDiscarded — so how often it still happens is measurable in
//    Clarity even after this mitigation.

// ~-55 dBFS RMS: comfortably above the browser's silence threshold (~-72 dBFS)
// so the tab registers as audible, but far below anything a person perceives —
// especially at a sub-bass frequency in a background tab.
const KEEPALIVE_GAIN = 0.003;
const KEEPALIVE_FREQ = 40; // Hz — sub-bass; inaudible / barely reproduced by speakers

export default function useTabKeepAlive(enabled = true) {
  const ctxRef = useRef(null);
  const gainRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    // Was this very page load a browser-initiated discard/reload? Report it once.
    try {
      if (document.wasDiscarded) {
        window.clarity?.("event", "tab_was_discarded");
        console.warn("[RenderNow] This tab was discarded by the browser and reloaded.");
      }
    } catch (_) {}

    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return undefined; // No Web Audio support — nothing we can do; fail quietly.

    // Build the (osc -> gain -> output) graph once, the first time we're allowed
    // to. gain starts at 0 so nothing is emitted until the tab actually hides.
    const ensureGraph = () => {
      if (ctxRef.current) return;
      try {
        const ctx = new AC();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        gain.gain.value = 0;
        osc.frequency.value = KEEPALIVE_FREQ;
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        ctxRef.current = ctx;
        gainRef.current = gain;
      } catch (_) {}
    };

    // Audible only while the tab is hidden; silent (and cheap) while it's visible.
    const applyForVisibility = () => {
      const ctx = ctxRef.current;
      const gain = gainRef.current;
      if (!ctx || !gain) return;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      try {
        gain.gain.value = document.visibilityState === "hidden" ? KEEPALIVE_GAIN : 0;
      } catch (_) {}
    };

    // Autoplay policy: unlock the context on the first genuine user gesture, then
    // it stays alive for the tab's lifetime and we just toggle gain by visibility.
    const unlock = () => {
      ensureGraph();
      applyForVisibility();
    };

    const gestureOpts = { passive: true };
    const gestureEvents = ["pointerdown", "keydown", "touchstart"];
    gestureEvents.forEach((e) => window.addEventListener(e, unlock, gestureOpts));
    document.addEventListener("visibilitychange", applyForVisibility);

    return () => {
      gestureEvents.forEach((e) => window.removeEventListener(e, unlock, gestureOpts));
      document.removeEventListener("visibilitychange", applyForVisibility);
      try { ctxRef.current?.close(); } catch (_) {}
      ctxRef.current = null;
      gainRef.current = null;
    };
  }, [enabled]);
}
